// Several reads, one screen.
//
// `reportUsageCost` answers for ONE deployment and ONE tenant, because the arm
// that reports a billed month resolves the run that closed it, and a run belongs
// to one deployment (a tenant slug is unique only inside one cluster). A deal
// binds several (deployment, tenant) pairs, so its screen is several reads, and
// this is where they become one answer.
//
// Adding them is sound because the rollup rows behind two pairs are disjoint:
// `crm.usage_daily_cost` is keyed on (deployment, tenant, day, …), so a chip
// that goes on adds rows to a sum and never re-prices a fact. The one thing this
// may not do is add two currencies, and it does not: it reports both and leaves
// the screen to refuse the total.

import type {
  UsageCostGroup,
  UsageCostPending,
  UsageCostProvenance,
  UsageCostResponse,
  UsageFx,
  UsageRateGap,
} from "@crm/shared";

/**
 * The rate a merged answer says it was taken at: the most recent one any part
 * applied, which is how the server picks the one rate a report echoes. Null when
 * no part converted anything.
 */
export function latestUsageFx(fxs: readonly (UsageFx | null)[]): UsageFx | null {
  let latest: UsageFx | null = null;
  for (const fx of fxs) {
    if (fx !== null && (latest === null || fx.as_of > latest.as_of)) latest = fx;
  }
  return latest;
}

/**
 * The two declared arms of one operation. Which arm answered is the HTTP status
 * and not a field on a body, so the api layer maps the status and every screen
 * reads this union.
 */
export type UsageCostAnswer =
  | { readonly kind: "ready"; readonly report: UsageCostResponse }
  | { readonly kind: "pending"; readonly pending: UsageCostPending };

export interface UsageCostUnion {
  /** The union of every part that had figures, or null when none of them did. */
  readonly report: UsageCostResponse | null;
  /** The jobs still filling a part of the scope, in the order the parts were asked for. */
  readonly jobs: readonly UsageCostPending[];
  /** True while any part is being recomputed: the figures are the previous answer. */
  readonly stale: boolean;
  /** Every currency the parts reported. More than one and no total may be drawn. */
  readonly currencies: readonly string[];
}

interface MutableGroup {
  costMinor: number;
  factsScanned: number;
  factsMatched: number;
  factsPriced: number;
  nullField: number;
  rateUnavailable: number;
  divisionByZero: number;
  units: number | null;
  audioSeconds: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  characters: number | null;
}

/** Null plus a number is that number; null plus null stays null, because nobody measured it. */
function addOptional(left: number | null, right: number | null): number | null {
  if (left === null) return right;
  if (right === null) return left;
  return left + right;
}

function emptyGroup(): MutableGroup {
  return {
    costMinor: 0,
    factsScanned: 0,
    factsMatched: 0,
    factsPriced: 0,
    nullField: 0,
    rateUnavailable: 0,
    divisionByZero: 0,
    units: null,
    audioSeconds: null,
    inputTokens: null,
    outputTokens: null,
    characters: null,
  };
}

function addGroup(into: MutableGroup, group: UsageCostGroup): void {
  into.costMinor += group.cost_minor;
  into.factsScanned += group.facts_scanned;
  into.factsMatched += group.facts_matched;
  into.factsPriced += group.facts_priced;
  into.nullField += group.unpriced_by_reason.null_field;
  into.rateUnavailable += group.unpriced_by_reason.rate_unavailable;
  into.divisionByZero += group.unpriced_by_reason.division_by_zero;
  into.units = addOptional(into.units, group.units);
  into.audioSeconds = addOptional(into.audioSeconds, group.audio_seconds);
  into.inputTokens = addOptional(into.inputTokens, group.input_tokens);
  into.outputTokens = addOptional(into.outputTokens, group.output_tokens);
  into.characters = addOptional(into.characters, group.characters);
}

function toWire(key: string, group: MutableGroup): UsageCostGroup {
  return {
    key,
    cost_minor: group.costMinor,
    facts_scanned: group.factsScanned,
    facts_matched: group.factsMatched,
    facts_priced: group.factsPriced,
    units: group.units,
    audio_seconds: group.audioSeconds,
    input_tokens: group.inputTokens,
    output_tokens: group.outputTokens,
    characters: group.characters,
    unpriced_by_reason: {
      null_field: group.nullField,
      rate_unavailable: group.rateUnavailable,
      division_by_zero: group.divisionByZero,
    },
  };
}

/**
 * The provenance of a union.
 *
 * `closed` means BILLED, and half a scope that has been billed is not a billed
 * scope: the header would say "as billed" over a figure that is still moving.
 * The run id survives only when one run closed every part, because "run 3f9a…"
 * naming one of two runs is worse than naming none.
 */
function mergeProvenance(reports: readonly UsageCostResponse[]): UsageCostProvenance {
  const every = reports.every((report) => report.provenance.source === "closed");
  const runIds = new Set(
    reports
      .map((report) => report.provenance.closed_run_id)
      .filter((id): id is string => id !== null),
  );
  const computedAt = reports
    .map((report) => report.provenance.computed_at)
    .filter((at): at is string => at !== null)
    .sort();
  return {
    source: every && reports.length > 0 ? "closed" : "rollup",
    // The OLDEST part, because an answer is only as fresh as its stalest half.
    computed_at: computedAt[0] ?? null,
    stale_days: reports.reduce((sum, report) => sum + report.provenance.stale_days, 0),
    closed_run_id: every && runIds.size === 1 ? ([...runIds][0] ?? null) : null,
  };
}

function gapKey(gap: UsageRateGap): string {
  return [gap.deployment_slug, gap.provider, gap.operation, gap.model ?? ""].join(" ");
}

export function mergeUsageCost(answers: readonly UsageCostAnswer[]): UsageCostUnion {
  const jobs = answers.flatMap((answer) => (answer.kind === "pending" ? [answer.pending] : []));
  // A pending part still carries its previous answer, and that answer counts: a
  // person who can already see roughly the right number can keep working, which
  // is the whole reason the 202 carries one.
  const reports = answers.flatMap((answer) =>
    answer.kind === "ready"
      ? [answer.report]
      : answer.pending.stale_result === null
        ? []
        : [answer.pending.stale_result],
  );

  const currencies = [...new Set(reports.map((report) => report.currency))];
  if (reports.length === 0) {
    return { report: null, jobs, stale: jobs.length > 0, currencies };
  }

  const merged = new Map<string, MutableGroup>();
  for (const report of reports) {
    for (const group of report.groups) {
      const bucket = merged.get(group.key) ?? emptyGroup();
      addGroup(bucket, group);
      merged.set(group.key, bucket);
    }
  }
  const groups = [...merged.entries()]
    .map(([key, group]) => toWire(key, group))
    .sort((left, right) => right.cost_minor - left.cost_minor || left.key.localeCompare(right.key));

  const costMinor = reports.reduce((sum, report) => sum + report.cost_minor, 0);
  const calls = reports.reduce((sum, report) => sum + report.calls, 0);
  const omittedCount = reports.reduce((sum, report) => sum + report.groups_omitted.count, 0);
  const gaps = new Map<string, UsageRateGap>();
  for (const report of reports) {
    for (const gap of report.rate_gaps) gaps.set(gapKey(gap), gap);
  }

  const first = reports[0];
  return {
    report: {
      period: first === undefined ? "" : first.period,
      // The screen refuses the total when `currencies` holds more than one, so
      // this names the first rather than inventing a mixed-currency label.
      currency: currencies[0] ?? "CAD",
      fx: latestUsageFx(reports.map((report) => report.fx)),
      calls,
      cost_minor: costMinor,
      // An average over no calls is undefined. Zero would read as "a call was
      // free", which is the one answer that is definitely wrong.
      cost_per_call_minor: calls === 0 ? null : costMinor / calls,
      groups,
      // An upper bound when two parts cut the same key, and exact whenever
      // nothing was cut — which is every read this app makes, because the
      // screens ask for the maximum the operation allows.
      groups_total: groups.length + omittedCount,
      groups_omitted: {
        count: omittedCount,
        cost_minor: reports.reduce((sum, report) => sum + report.groups_omitted.cost_minor, 0),
      },
      telephony_phases: {
        ai_leg_cost_minor: reports.reduce(
          (sum, r) => sum + r.telephony_phases.ai_leg_cost_minor,
          0,
        ),
        post_transfer_cost_minor: reports.reduce(
          (sum, r) => sum + r.telephony_phases.post_transfer_cost_minor,
          0,
        ),
        ai_leg_seconds: reports.reduce((sum, r) => sum + r.telephony_phases.ai_leg_seconds, 0),
        post_transfer_seconds: reports.reduce(
          (sum, r) => sum + r.telephony_phases.post_transfer_seconds,
          0,
        ),
        unsegmented_legs: reports.reduce((sum, r) => sum + r.telephony_phases.unsegmented_legs, 0),
      },
      meters: [...new Set(reports.flatMap((report) => report.meters))].sort(),
      provenance: mergeProvenance(reports),
      rate_gaps: [...gaps.values()],
    },
    jobs,
    stale: jobs.length > 0,
    currencies,
  };
}
